import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/pages/it/Aset";
import { CheckCircle2, Power, PowerOff } from "lucide-react";
import { toast } from "sonner";
import { SearchBar, matchesSearch, usePagination, PaginationNav, PAGE_SIZE } from "@/components/tableUtils";

export default function Maintenance() {
  const [assets, setAssets] = useState([]);
  const [logs, setLogs] = useState([]);

  const load = () => {
    api.get("/assets").then((r) => setAssets(r.data.filter((a) => a.status === "Rusak" || a.status === "Perbaikan"))).catch((e) => toast.error(apiError(e)));
    api.get("/maintenance-logs").then((r) => setLogs(r.data.slice(0, 15))).catch(() => {});
  };
  useEffect(load, []);

  const [search, setSearch] = useState("");
  const filteredAssets = assets.filter((a) => matchesSearch(a, ["kode", "nama", "ruangan"], search));
  const filteredLogs = logs.filter((l) => matchesSearch(l, ["asset_kode", "asset_nama", "asset_ruangan", "detail_pekerjaan"], search));
  const assetsPg = usePagination(filteredAssets.length);
  const logsPg = usePagination(filteredLogs.length);
  const pagedAssets = filteredAssets.slice((assetsPg.page - 1) * PAGE_SIZE, assetsPg.page * PAGE_SIZE);
  const pagedLogs = filteredLogs.slice((logsPg.page - 1) * PAGE_SIZE, logsPg.page * PAGE_SIZE);

  const setActive = async (a, active) => {
    try {
      await api.patch(`/assets/${a.id}/active`, { is_active: active });
      toast.success(active ? "Aset diaktifkan" : "Aset dinonaktifkan");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const markFixed = async (a) => {
    try {
      await api.patch(`/assets/${a.id}/status`, { status: "Baik" });
      toast.success(`${a.nama} ditandai selesai diperbaiki`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="maintenance-page" className="space-y-8">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">Maintenance</h1>
        <p className="text-sm text-muted-foreground">Aset yang sedang dalam perbaikan atau berstatus rusak.</p>
      </div>

      <SearchBar value={search} onChange={(v) => { setSearch(v); assetsPg.setPage(1); logsPg.setPage(1); }} testid="maintenance-search" />

      <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="font-heading font-semibold text-lg">Aset Dalam Perbaikan</h2>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Kode</TableHead>
              <TableHead>Nama Aset</TableHead>
              <TableHead>Ruangan</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Aktif</TableHead>
              <TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pagedAssets.map((a) => (
              <TableRow key={a.id} data-testid={`maintenance-row-${a.kode}`}>
                <TableCell className="font-medium">{a.kode}</TableCell>
                <TableCell>{a.nama}</TableCell>
                <TableCell>{a.ruangan}</TableCell>
                <TableCell><StatusBadge status={a.status} /></TableCell>
                <TableCell>
                  <Badge variant={a.is_active ? "secondary" : "outline"} data-testid={`maintenance-active-${a.kode}`}>
                    {a.is_active ? "Aktif" : "Nonaktif"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" onClick={() => markFixed(a)} title="Selesai diperbaiki" data-testid={`maintenance-done-${a.kode}`}>
                      <CheckCircle2 className="w-4 h-4 text-primary" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setActive(a, !a.is_active)}
                      title={a.is_active ? "Nonaktifkan" : "Aktifkan"}
                      data-testid={`maintenance-toggle-${a.kode}`}
                    >
                      {a.is_active ? <PowerOff className="w-4 h-4 text-destructive" /> : <Power className="w-4 h-4 text-primary" />}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {filteredAssets.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-10" data-testid="maintenance-empty">
                  Tidak ada aset dalam perbaikan saat ini.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <PaginationNav page={assetsPg.page} pageCount={assetsPg.pageCount} onChange={assetsPg.setPage} testid="maintenance-pagination" />
      </div>

      <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="font-heading font-semibold text-lg">Riwayat Maintenance Terbaru</h2>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tanggal</TableHead>
              <TableHead>Aset</TableHead>
              <TableHead>Detail Pekerjaan</TableHead>
              <TableHead>Hasil</TableHead>
              <TableHead>Teknisi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pagedLogs.map((l) => (
              <TableRow key={l.id} data-testid={`log-row-${l.id.slice(0, 8)}`}>
                <TableCell>{l.tanggal}</TableCell>
                <TableCell>{l.asset_kode} — {l.asset_nama}</TableCell>
                <TableCell className="max-w-xs truncate">{l.detail_pekerjaan}</TableCell>
                <TableCell><StatusBadge status={l.status} /></TableCell>
                <TableCell>{l.teknisi || "—"}</TableCell>
              </TableRow>
            ))}
            {filteredLogs.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground py-10" data-testid="logs-empty">
                  Belum ada riwayat maintenance.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <PaginationNav page={logsPg.page} pageCount={logsPg.pageCount} onChange={logsPg.setPage} testid="maintenance-logs-pagination" />
      </div>
    </div>
  );
}
